/**
 * src/services/liveFeedAlgorithms.ts
 *
 * Pluggable ordering algorithms for the GeoTrust live-broadcast feed.
 *
 * The live directory ("Live now" overlay) and the TikTok-style
 * swipe-between-broadcasts control in the profile viewer both need
 * to answer the same question: given every active broadcast, what
 * order should they appear in?  This module decouples that decision
 * from the UI so we can add more sorting strategies later without
 * touching the consumers.
 *
 * Shipped algorithms:
 *
 *   - `recency`   — freshest first (startedAt DESC).  Safe default;
 *                   matches the user's expectation of "latest live".
 *   - `proximity` — nearest to the viewer's current GPS location
 *                   first (great-circle distance from approxLat/Lng).
 *                   Falls back to recency when either side has no
 *                   location; any session with no approx location
 *                   is sorted to the end.
 *   - `random`    — stable per-session shuffle.  We seed with the
 *                   session-id hash so opening the directory,
 *                   swiping "next", swiping "back", swiping "next"
 *                   again lands on the same order (otherwise the
 *                   neighbour arithmetic is non-deterministic).
 *
 * Adding a new algorithm:
 *
 *   1. Implement `LiveAlgorithm` and pass it to `registerLiveAlgorithm`.
 *   2. Users get it automatically in the directory's selector chip
 *      row + the next/prev controls in the watching view.
 */

import { LiveSession } from './liveSessionService';

export interface LiveFeedLocation { lat: number; lng: number; }

export interface LiveAlgorithmContext {
  sessions: LiveSession[];
  /** Viewer's current GPS position, if known. */
  viewerLocation?: LiveFeedLocation | null;
  /** Numeric seed.  Used by `random`; ignored by deterministic algos. */
  seed: number;
}

export interface LiveAlgorithm {
  id: string;
  label: string;
  icon: string;
  description: string;
  /** Pure: returns a new array sorted by this algorithm. */
  sort(ctx: LiveAlgorithmContext): LiveSession[];
}

// ── Built-ins ────────────────────────────────────────────────────

function sessionStartedMs(s: LiveSession): number {
  return (s.startedAt ? Date.parse(s.startedAt) : Date.parse(s.createdAt)) || 0;
}

function haversineMeters(a: LiveFeedLocation, b: LiveFeedLocation): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la1  = toRad(a.lat);
  const la2  = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Mulberry32 — small deterministic PRNG seeded by a u32. */
function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

const recency: LiveAlgorithm = {
  id: 'recency',
  label: 'Recent',
  icon: '🕒',
  description: 'Freshest broadcasts first.',
  sort: ({ sessions }) => {
    return sessions.slice().sort((a, b) => sessionStartedMs(b) - sessionStartedMs(a));
  },
};

const proximity: LiveAlgorithm = {
  id: 'proximity',
  label: 'Nearby',
  icon: '📍',
  description: 'Closest to your current location first.',
  sort: ({ sessions, viewerLocation }) => {
    if (!viewerLocation) return recency.sort({ sessions, seed: 0 });
    // Annotate once, then sort — cheaper than computing haversine
    // twice per comparison.
    const annotated = sessions.map((s) => {
      if (s.approxLat == null || s.approxLng == null) {
        return { s, d: Number.POSITIVE_INFINITY };
      }
      return {
        s,
        d: haversineMeters(viewerLocation, { lat: s.approxLat, lng: s.approxLng }),
      };
    });
    annotated.sort((a, b) => {
      if (a.d !== b.d) return a.d - b.d;
      // Tiebreaker: recency, so multiple broadcasts in the same cell
      // still have a stable order between polls.
      return sessionStartedMs(b.s) - sessionStartedMs(a.s);
    });
    return annotated.map(x => x.s);
  },
};

const random: LiveAlgorithm = {
  id: 'random',
  label: 'Shuffle',
  icon: '🎲',
  description: 'Random order — discovery mode.',
  sort: ({ sessions, seed }) => {
    const rng = mulberry32(seed || 1);
    // Fisher–Yates
    const arr = sessions.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  },
};

// ── Registry ─────────────────────────────────────────────────────

const _registry = new Map<string, LiveAlgorithm>();

export function registerLiveAlgorithm(algo: LiveAlgorithm): void {
  _registry.set(algo.id, algo);
}

export function getLiveAlgorithm(id: string): LiveAlgorithm | undefined {
  return _registry.get(id);
}

export function listLiveAlgorithms(): LiveAlgorithm[] {
  return Array.from(_registry.values());
}

registerLiveAlgorithm(recency);
registerLiveAlgorithm(proximity);
registerLiveAlgorithm(random);

export const DEFAULT_ALGORITHM_ID = 'recency';
